/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Alert } from "react-bootstrap";
import parse, { domToReact } from "html-react-parser";
import { useNavigate } from "react-router-dom";

import {
  getAuthenticatedWebSocketUrl,
  getUserId,
} from "../../utilities/authHelper";
import { fetchHomeSuggestions } from "../../utilities/ApiUtilities";
import useChatVoice, {
  htmlToSpeechText,
} from "../../utilities/useChatVoice";
import LinkPreview from "../ProductDashboard/Emgpt/LinkPreview";
import CollapsibleDiv, {
  ThinkingIndicator,
} from "../ProductDashboard/Emgpt/CollapsibleDiv";
import HighchartFromText from "../ProductDashboard/Emgpt/HighchartFromText";
import SourceChips from "../ProductDashboard/Emgpt/SourceChips";
import { giveFeedback } from "../../utilities/EmgptUtilities";
import {
  buildProductSuggestionsForPair,
} from "../../utilities/productRecommendationUtilities";
import {
  getChatSuggestionsFromPayload,
  normalizeChatSuggestion,
  normalizeChatSuggestions,
} from "../../utilities/chatSuggestionUtilities";
import { useResolvedDark } from "../../ThemeProvider/ThemeContext";
import emalphaMark from "../../../assets/logo-collapsed.png";

// =========================
// Render helper
// =========================
const ANALYSIS_BLOCK_RE = /<collapsiblediv>([\s\S]*?)(?:<\/collapsiblediv>|$)/gi;
const SOURCE_LIST_RE = /<sourcelist urls="([^"]*)"><\/sourcelist>/gi;

const escapeAttr = (value) =>
  String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;");

// The stream can open several analysis blocks per answer (metadata frames,
// deeper-search rounds) and several url batches. Fold them into one analysis
// block at the top and collect the source urls separately.
const consolidateAnswerHtml = (html) => {
  const urls = [];
  const withoutSources = html.replace(SOURCE_LIST_RE, (_, list) => {
    urls.push(...list.split(/\s+/).filter(Boolean));
    return "";
  });

  const stepChunks = [];
  const withoutAnalysis = withoutSources.replace(ANALYSIS_BLOCK_RE, (_, inner) => {
    if (inner.trim()) stepChunks.push(inner.trim());
    return "";
  });

  const hasAnalysis = stepChunks.length > 0 || /<collapsiblediv>/i.test(html);
  const analysis = hasAnalysis
    ? `<collapsiblediv>${stepChunks.join("\n")}</collapsiblediv>`
    : "";

  return { html: `${analysis}${withoutAnalysis.trim()}`, hasAnalysis, urls };
};

// Sources sit inside the analysis block, below its steps. Without an analysis
// block they fall back to a row after the answer, held back until it
// completes because they usually arrive before the answer text.
export const RenderFromString = ({ htmlString, isStreaming, isComplete = true }) => {
  if (typeof htmlString !== "string") return null;

  const { html, hasAnalysis, urls } = consolidateAnswerHtml(htmlString);
  const sources = urls.length ? <SourceChips urls={urls} /> : null;

  return (
    <>
      {parse(html, {
        replace: (domNode) => {
          if (domNode?.name === "linkpreview") {
            const url = domNode.attribs?.url || "#";
            return <LinkPreview url={url} />;
          }
          if (domNode?.name === "collapsiblediv") {
            return (
              <CollapsibleDiv
                isStreaming={isStreaming}
                footer={sources}
                variant="inline"
              >
                {domToReact(domNode.children)}
              </CollapsibleDiv>
            );
          }
          if (domNode?.name === "chartrender") {
            return (
              <HighchartFromText chartText={domNode.childNodes?.[0]?.data || ""} />
            );
          }
          return undefined;
        },
      })}
      {!hasAnalysis && isComplete ? sources : null}
    </>
  );
};

// True once an answer has body text beyond its analysis steps and sources.
const hasRenderedAnswer = (text) =>
  typeof text === "string" &&
  text.replace(ANALYSIS_BLOCK_RE, "").replace(SOURCE_LIST_RE, "").trim().length > 0;

// Label for the in-progress indicator while an answer is still loading. It
// stays visible after text starts, so a stalled stream still reads as working.
const getPendingAnswerLabel = (text) => {
  const trimmed = typeof text === "string" ? text.trim() : "";
  if (!trimmed) return "Thinking";
  return hasRenderedAnswer(trimmed) ? "Generating" : "Generating answer";
};

const tryParseJsonObject = (value) => {
  if (typeof value !== "string") return null;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const parseActionPayload = (payload) => {
  const candidates = [];

  if (payload && typeof payload === "object") {
    candidates.push(payload);
  }

  if (payload?.message && typeof payload.message === "object") {
    candidates.push(payload.message);
  }

  const parsedMessage = tryParseJsonObject(payload?.message);
  if (parsedMessage) {
    candidates.push(parsedMessage);
  }

  for (const candidate of candidates) {
    const resumeId =
      typeof candidate?.resume_id === "string"
        ? candidate.resume_id.trim()
        : "";
    const actions = Array.isArray(candidate?.actions)
      ? candidate.actions
          .filter((action) => typeof action === "string" && action.trim())
          .map((action) => action.trim())
      : [];
    const payloadType =
      typeof payload?.type === "string" ? payload.type.trim() : "";
    const candidateType =
      typeof candidate?.type === "string" ? candidate.type.trim() : "";
    const looksLikeActionPrompt =
      payloadType === "action" ||
      candidateType === "action" ||
      candidateType === "continue_search" ||
      actions.includes("continue") ||
      actions.includes("answer_now");

    if (!resumeId || !looksLikeActionPrompt) continue;

    return {
      action:
        typeof candidate?.action === "string"
          ? candidate.action.trim()
          : "continue",
      actions: actions.length > 0 ? actions : ["continue", "answer_now"],
      checkpoint:
        typeof candidate?.checkpoint === "string"
          ? candidate.checkpoint.trim()
          : "",
      currentSourceCount: Number.isFinite(candidate?.current_source_count)
        ? candidate.current_source_count
        : null,
      expiresAt:
        typeof candidate?.expires_at === "string"
          ? candidate.expires_at.trim()
          : "",
      expiresInSeconds: Number.isFinite(candidate?.expires_in_seconds)
        ? candidate.expires_in_seconds
        : null,
      message:
        typeof candidate?.message === "string"
          ? candidate.message.trim()
          : "",
      questionMode:
        typeof candidate?.question_mode === "string"
          ? candidate.question_mode.trim()
          : "",
      resumeId,
      type: candidateType || payloadType || "action",
    };
  }

  return null;
};

const DEFAULT_PLACEHOLDER_QUESTIONS = [
  "What moved USD/JPY overnight?",
  "What are today's key levels for EUR/USD?",
  "What is driving the S&P 500 today?",
  "What changed in 10Y U.S. Treasury yields today?",
  "Why is gold moving today?",
  "Why is crude oil moving today?",
  "What is VIX signaling right now?",
  "How is the Nasdaq 100 trading today?",
  "What is priced in for the next Fed meeting?",
  "What is the outlook for the U.S. dollar today?",
];
const VOICE_IDLE_AUTO_SEND_MS = 1000;
// Frames that carry bookkeeping, not visible content; they must not break an
// in-progress analysis block.
const METADATA_FRAME_TYPES = new Set(["MESSAGE_ID", "SUGGESTIONS"]);
// Thin-evidence prompts are answered automatically: search deeper this many
// times, then answer with what we have.
const MAX_AUTO_CONTINUE_ROUNDS = 2;
const EMPTY_RESPONSE_RETRY_LIMIT = 1;
const EMPTY_RESPONSE_FALLBACK_TEXT =
  "No visible response was received. Please try again.";

// =========================
// Input Bar (themed)
// =========================
const HomeChatInputBar = React.memo(function HomeChatInputBar({
  docked,
  placeholder,
  placeholderQuestions,
  animatePlaceholder,
  userInput,
  setUserInput,
  isLoading,
  isListening,
  isSpeechRecognitionSupported,
  onToggleListening,
  onSend,
  onStop,
  onPlaceholderClick,
  ui,
}) {
  const cleanedPlaceholderSuggestions = useMemo(
    () =>
      normalizeChatSuggestions(placeholderQuestions),
    [placeholderQuestions],
  );

  const [ghostText, setGhostText] = useState("");
  const [ghostLeaving, setGhostLeaving] = useState(false);
  const [ghostTyping, setGhostTyping] = useState(false);
  const currentFullSuggestionRef = useRef(null);
  const suggestionsRef = useRef(cleanedPlaceholderSuggestions);
  const questionIndexRef = useRef(0);
  suggestionsRef.current = cleanedPlaceholderSuggestions;

  // Restart only when the actual question list changes, not on every new
  // array reference (e.g. when API suggestions merge in).
  const suggestionSignature = cleanedPlaceholderSuggestions
    .map((suggestion) => suggestion.question)
    .join("\n");
  const hasUserInput = (userInput || "").length > 0;
  const isGhostActive =
    animatePlaceholder && cleanedPlaceholderSuggestions.length > 0;

  useEffect(() => {
    if (!isGhostActive || hasUserInput) return undefined;

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let timerId;
    let cancelled = false;
    const schedule = (fn, delay) => {
      timerId = window.setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
    };

    // Slightly irregular cadence reads as natural typing rather than a
    // metronome; brief pauses after word and clause boundaries.
    const getTypingDelay = (previousChar) => {
      let delay = 26 + Math.random() * 30;
      if (previousChar === " ") delay += 18;
      if (/[,.?!:;]/.test(previousChar)) delay += 140;
      return delay;
    };

    const startQuestion = () => {
      const list = suggestionsRef.current;
      if (!list.length) return;
      const index = questionIndexRef.current % list.length;
      const suggestion = list[index];
      const question = suggestion.question;
      currentFullSuggestionRef.current = suggestion;
      setGhostLeaving(false);

      const fadeOutAndAdvance = () => {
        setGhostTyping(false);
        setGhostLeaving(true);
        schedule(() => {
          questionIndexRef.current = index + 1;
          setGhostText("");
          startQuestion();
        }, 360);
      };

      if (prefersReducedMotion) {
        setGhostText(question);
        schedule(fadeOutAndAdvance, 4200);
        return;
      }

      let charIndex = 0;
      setGhostText("");
      setGhostTyping(true);
      const typeNext = () => {
        charIndex += 1;
        setGhostText(question.slice(0, charIndex));
        if (charIndex >= question.length) {
          setGhostTyping(false);
          schedule(fadeOutAndAdvance, 2600);
          return;
        }
        schedule(typeNext, getTypingDelay(question[charIndex - 1]));
      };
      schedule(typeNext, 180);
    };

    schedule(startQuestion, 300);

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
      setGhostText("");
      setGhostTyping(false);
      setGhostLeaving(false);
    };
  }, [isGhostActive, hasUserInput, suggestionSignature]);

  const showGhost = isGhostActive && !hasUserInput;
  const collapsedPillBorder = ui.collapsedPillBorder || (ui.isDark
    ? "rgba(98,142,255,0.45)"
    : "rgba(13,110,253,0.26)");
  const collapsedPillShadow = ui.collapsedPillShadow || (ui.isDark
    ? "0 16px 34px rgba(0,0,0,0.44), 0 0 0 1px rgba(88,132,255,0.18)"
    : "0 14px 30px rgba(13,110,253,0.12), 0 2px 8px rgba(16,24,40,0.08)");
  const collapsedPillBg = ui.collapsedPillBg || (ui.isDark
    ? "linear-gradient(180deg, rgba(28,40,76,0.98) 0%, rgba(17,25,50,0.98) 100%)"
    : "linear-gradient(180deg, rgba(238,246,255,0.98) 0%, rgba(250,253,255,0.98) 100%)");

  const hasText = Boolean((userInput ?? "").trim());

  return (
    <div
      className={`w-100 ea-composer ${docked ? "is-docked" : "is-launcher"}`}
      style={docked ? undefined : { padding: "8px 2px" }}
    >
      <div
        className="ea-composer__field"
        style={
          docked
            ? undefined
            : {
                border: `1px solid ${collapsedPillBorder}`,
                boxShadow: collapsedPillShadow,
                background: collapsedPillBg,
              }
        }
      >
        {!docked && (
          <span className="ea-composer__lead" aria-hidden="true">
            <i className="fa-solid fa-magnifying-glass" />
          </span>
        )}

        <div className="ea-composer__input-wrap">
          <input
            id="homeChatInput"
            className={`form-control home-chat-input ea-composer__input ${ui.isDark ? "is-dark" : "is-light"}`}
            type="text"
            placeholder={isGhostActive ? "" : placeholder}
            aria-label={placeholder}
            value={userInput ?? ""}
            onChange={(e) => setUserInput(e.target.value)}
            onClick={() => {
              if (
                !(userInput ?? "").trim() &&
                currentFullSuggestionRef.current &&
                onPlaceholderClick
              ) {
                onPlaceholderClick(currentFullSuggestionRef.current);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSend();
            }}
            disabled={isLoading}
            autoComplete="off"
            style={{
              color: ui.isDark ? "#E8ECF8" : "#111827",
              cursor: !(userInput ?? "").trim() && animatePlaceholder ? "pointer" : "text",
            }}
          />
          {showGhost && (
            <span
              className={`ea-composer__ghost${ghostLeaving ? " is-leaving" : ""}${
                ghostTyping ? " is-typing" : ""
              }`}
              aria-hidden="true"
            >
              <span className="ea-composer__ghost-text">{ghostText}</span>
              {ghostText && <span className="ea-composer__caret" />}
            </span>
          )}
        </div>

        {isSpeechRecognitionSupported && (
          <button
            type="button"
            className={`ea-composer__icon ${isListening ? "is-active" : ""}`}
            onClick={onToggleListening}
            title={isListening ? "Stop voice input" : "Voice input"}
            aria-label={isListening ? "Stop voice input" : "Start voice input"}
            aria-pressed={isListening}
            disabled={isLoading}
          >
            <i className="fa-solid fa-microphone" aria-hidden="true" />
          </button>
        )}

        {isLoading ? (
          <button
            type="button"
            className="ea-composer__send is-stop"
            onClick={onStop}
            title="Stop generating"
            aria-label="Stop generating"
          >
            <span className="ea-composer__stop-glyph" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="ea-composer__send"
            onClick={onSend}
            disabled={!hasText}
            title="Send"
            aria-label="Send message"
          >
            <i className="fa-solid fa-arrow-up" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
});

// =========================
// Main Component (themed)
// =========================
const HomeChat = React.forwardRef(function HomeChat({
  placeholder = "Ask EMAlpha…",
  placeholderQuestions = DEFAULT_PLACEHOLDER_QUESTIONS,
  useApiSuggestionsOnly = false,
  animatePlaceholder = true,
  origin = "HOME",
  defaultCompanyIds = [""],
  defaultDashboardName = "",
  defaultCountries = [""],
  expandedHeight = 520,
  expandedPresentation = "inline",
  expandedOverlayTop = 118,
  expandedOverlayWidth = "min(1080px, calc(100vw - 320px))",
  keepLauncherVisibleWhenExpanded = false,
  // App-level host instance: renders only the conversation panel and dock.
  hideLauncher = false,
  // Launcher-only instance: hands questions to the app-level assistant and
  // returns true when it accepted them.
  onDelegateSend,
  inputBarHeight = 66,
  showSuggestionsDefault = true,

  // ✅ new (so HomeMobile can pass it like CoTraderMobile)
  isDark: isDarkProp,

  // ✅ optional (if you want to reuse HomeMobile palette)
  themeStyles,

  // tabs from dashboard context (for access-aware product suggestions)
  availableTabs = [],
}, ref) {
  const isDark = useResolvedDark(isDarkProp);
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  // Minimized keeps the conversation alive but docks it bottom-right so the
  // page underneath stays usable.
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasUnreadAnswer, setHasUnreadAnswer] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [apiPlaceholderSuggestions, setApiPlaceholderSuggestions] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const [feedbackMessageId, setFeedbackMessageId] = useState([]);
  const [feedbackTexts, setFeedbackTexts] = useState({});
  const [feedbackTypes, setFeedbackTypes] = useState({});

  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const showSuggestions = showSuggestionsDefault;
  const [summary, setSummary] = useState("");
  const [summaryVersion, setSummaryVersion] = useState(0);

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const dockButtonRef = useRef(null);
  const wasLoadingRef = useRef(false);
  // Bumped on every send/stop/close; stale sockets and timers compare against it.
  const requestSeqRef = useRef(0);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const copiedTimerRef = useRef(null);
  const voiceAutoSendTimerRef = useRef(null);
  const sendMessageRef = useRef(null);
  const handleSendMessageRef = useRef(null);
  const pendingSuggestionRef = useRef(null);

  const userLanguage = localStorage.getItem("user_language") || "English";
  const {
    clearSpeechError,
    isListening,
    isSpeechRecognitionSupported,
    isTtsEnabled,
    resetVoiceInput,
    speakStreamingText,
    speechError,
    stopListening,
    stopSpeaking,
    toggleListening,
  } = useChatVoice({
    userInput,
    setUserInput,
    userLanguage,
  });
  const fallbackPlaceholderSuggestions = useMemo(
    () =>
      normalizeChatSuggestions(
        useApiSuggestionsOnly ? [] : placeholderQuestions || [],
      ),
    [placeholderQuestions, useApiSuggestionsOnly],
  );
  const mergedPlaceholderSuggestions = useMemo(() => {
    const seenQuestions = new Set();

    return [
      ...fallbackPlaceholderSuggestions,
      ...apiPlaceholderSuggestions,
    ].filter((suggestion) => {
        const dedupeKey = suggestion.question.toLowerCase();
        if (seenQuestions.has(dedupeKey)) return false;

        seenQuestions.add(dedupeKey);
        return true;
      });
  }, [apiPlaceholderSuggestions, fallbackPlaceholderSuggestions]);
  const lastAnswerForSpeech = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.type !== "answer") continue;
      return {
        text: message?.text || "",
        streamKey: message?.message_id || `answer-${i}`,
      };
    }
    return null;
  }, [messages]);

  // -------------------------
  // Theme tokens
  // -------------------------
  const ui = useMemo(() => {
    if (themeStyles) {
      const isDarkGuess =
        themeStyles?.pageBg?.toLowerCase?.() === "#0b0f1a" ? true : isDark;

      return {
        isDark: isDarkGuess,

        cardBg: themeStyles.cardBg,
        cardBorder: themeStyles.cardBorder,
        divider: themeStyles.divider,
        text: themeStyles.sectionTitle,
        muted: themeStyles.muted,
        link: themeStyles.link,
        chip: themeStyles.chip,
        collapsedPillBg: themeStyles.collapsedPillBg,
        collapsedPillBorder: themeStyles.collapsedPillBorder,
        collapsedPillShadow: themeStyles.collapsedPillShadow,

        // pill/input bar
        pillBg: themeStyles.cardBg,
        pillBorder: themeStyles.cardBorder,
        pillShadow: isDarkGuess
          ? "0 10px 30px rgba(0, 0, 0, 0.35)"
          : "0 10px 30px rgba(16, 24, 40, 0.06)",

        dockedBg: isDarkGuess
          ? "rgba(11,15,26,0.80)"
          : "rgba(255,255,255,0.92)",

        // primary action
        primary: isDarkGuess ? "#4C7DFF" : "#111827",
        primaryText: "#FFFFFF",
        primaryBorder: isDarkGuess
          ? "rgba(76,125,255,0.9)"
          : "rgba(17,24,39,0.9)",
        primaryShadow: isDarkGuess
          ? "0 10px 24px rgba(76,125,255,0.25)"
          : "0 10px 24px rgba(17,24,39,0.18)",

        // backgrounds
        panelBg: isDarkGuess ? "#0B0F1A" : "#FFFFFF",
        panelGrad: isDarkGuess
          ? "linear-gradient(180deg, rgba(18,23,42,0.9) 0%, rgba(11,15,26,1) 55%)"
          : "linear-gradient(180deg, rgba(249,250,251,0.7) 0%, rgba(255,255,255,1) 45%)",

        // bubble shadows
        qShadow: isDarkGuess
          ? "0 10px 24px rgba(76,125,255,0.22)"
          : "0 10px 24px rgba(59,130,246,0.18)",
        aShadow: isDarkGuess
          ? "0 10px 24px rgba(0,0,0,0.30)"
          : "0 10px 24px rgba(16,24,40,0.08)",
      };
    }

    // fallback tokens
    if (isDark) {
      return {
        isDark: true,
        cardBg: "#0E1424",
        cardBorder: "#1B2440",
        divider: "#151D34",
        text: "#EEF2FC",
        muted: "#8F9ABD",
        link: "#7EA6FF",
        chip: "#7EA6FF",

        pillBg: "#0E1424",
        pillBorder: "#28335A",
        pillShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
        dockedBg: "rgba(11,15,26,0.80)",

        primary: "#4C7DFF",
        primaryText: "#FFFFFF",
        primaryBorder: "rgba(76,125,255,0.9)",
        primaryShadow: "0 10px 24px rgba(76,125,255,0.25)",

        panelBg: "#0A0F1C",
        panelGrad:
          "linear-gradient(180deg, rgba(18,26,45,0.9) 0%, rgba(10,15,28,1) 55%)",

        qShadow: "0 10px 24px rgba(76,125,255,0.22)",
        aShadow: "0 10px 24px rgba(0,0,0,0.30)",
      };
    }

    return {
      isDark: false,
      cardBg: "#FFFFFF",
      cardBorder: "#EEF2F7",
      divider: "#EEF2F7",
      text: "#0B1220",
      muted: "#6C757D",
      link: "#0D6EFD",
      chip: "#0D6EFD",

      pillBg: "#FFFFFF",
      pillBorder: "rgba(0,0,0,0.08)",
      pillShadow: "0 10px 30px rgba(16, 24, 40, 0.06)",
      dockedBg: "rgba(255,255,255,0.92)",

      primary: "#111827",
      primaryText: "#FFFFFF",
      primaryBorder: "rgba(17,24,39,0.9)",
      primaryShadow: "0 10px 24px rgba(17,24,39,0.18)",

      panelBg: "#FFFFFF",
      panelGrad:
        "linear-gradient(180deg, rgba(249,250,251,0.7) 0%, rgba(255,255,255,1) 45%)",

      qShadow: "0 10px 24px rgba(59,130,246,0.18)",
      aShadow: "0 10px 24px rgba(16,24,40,0.08)",
    };
  }, [isDark, themeStyles]);
  const chatSurfaceVars = useMemo(
    () => ({
      "--ea-chat-page-bg": ui.panelBg,
      "--ea-chat-surface": ui.cardBg,
      "--ea-chat-surface-alt": ui.isDark ? "rgba(17, 26, 51, 0.96)" : "#F6FAFF",
      "--ea-chat-border": ui.cardBorder,
      "--ea-chat-text": ui.text,
      "--ea-chat-muted": ui.muted,
      "--ea-chat-link": ui.link,
      "--ea-chat-accent": ui.isDark ? "#6F8CFF" : "#0D6EFD",
      "--ea-chat-accent-soft": ui.isDark
        ? "rgba(76, 125, 255, 0.18)"
        : "rgba(13, 110, 253, 0.12)",
      "--ea-chat-question-bg": ui.isDark
        ? "linear-gradient(135deg, #3158DC 0%, #567EFF 100%)"
        : "linear-gradient(135deg, #0D6EFD 0%, #175CD3 100%)",
      "--ea-chat-question-text": "#FFFFFF",
      "--ea-chat-answer-bg": ui.isDark ? "rgba(18, 23, 42, 0.92)" : "rgba(255, 255, 255, 0.94)",
      "--ea-chat-answer-text": ui.text,
      "--ea-chat-body-bg": ui.panelGrad,
      "--ea-chat-composer-bg": ui.dockedBg,
      "--ea-chat-input-bg": ui.isDark ? "rgba(11, 15, 26, 0.56)" : "#FFFFFF",
      "--ea-chat-chip-bg": ui.isDark ? "rgba(76, 125, 255, 0.12)" : "#F5F9FF",
      "--ea-chat-ghost-bg": ui.isDark ? "rgba(11, 15, 26, 0.72)" : "rgba(255, 255, 255, 0.88)",
      "--ea-chat-toast-bg": ui.isDark ? "#111A33" : "#0B1220",
      "--ea-chat-shadow": ui.isDark
        ? "0 26px 72px rgba(0, 0, 0, 0.42)"
        : "0 24px 72px rgba(16, 24, 40, 0.16)",
    }),
    [ui],
  );

  useEffect(() => {
    if (!isTtsEnabled || !lastAnswerForSpeech) return;
    const textForSpeech = htmlToSpeechText(lastAnswerForSpeech.text);
    speakStreamingText(textForSpeech, {
      isFinal: !isLoading,
      streamKey: lastAnswerForSpeech.streamKey,
    });
  }, [isLoading, isTtsEnabled, lastAnswerForSpeech, speakStreamingText]);

  useEffect(
    () => () => {
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
      requestSeqRef.current += 1;
      window.clearTimeout(copiedTimerRef.current);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
      stopListening();
      stopSpeaking();
    },
    [stopListening, stopSpeaking],
  );


  useEffect(() => {
    if (!isExpanded || isMinimized) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (isLoading) stopStreaming();
      else closeChat();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // Flag answers that finish while the assistant is docked.
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading && isMinimized) {
      setHasUnreadAnswer(true);
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, isMinimized]);

  // Move focus to the dock on minimize; return to the latest message on restore.
  useEffect(() => {
    if (!isExpanded) return;
    if (isMinimized) {
      dockButtonRef.current?.focus({ preventScroll: true });
    } else {
      messagesEndRef.current?.scrollIntoView({ block: "end" });
    }
  }, [isExpanded, isMinimized]);

  useEffect(() => {
    if (hideLauncher) return undefined;
    let isActive = true;

    const loadHomeSuggestions = async () => {
      try {
        const data = await fetchHomeSuggestions();
        const fetchedSuggestions = getChatSuggestionsFromPayload(data);

        if (isActive && fetchedSuggestions.length > 0) {
          setApiPlaceholderSuggestions(fetchedSuggestions);
        }
      } catch (error) {
        console.error("Error fetching home suggestions:", error);
      }
    };

    loadHomeSuggestions();

    return () => {
      isActive = false;
    };
  }, [hideLauncher]);

  // -------------------------
  // Actions
  // -------------------------
  const handlePlaceholderClick = (suggestion) => {
    const normalizedSuggestion = normalizeChatSuggestion(suggestion);
    if (!normalizedSuggestion) return;

    pendingSuggestionRef.current = normalizedSuggestion;
    setUserInput(normalizedSuggestion.question);
  };

  const handleUserInputChange = (nextUserInput) => {
    const pendingQuestion = pendingSuggestionRef.current?.question;
    if (pendingQuestion !== (nextUserInput || "").trim()) {
      pendingSuggestionRef.current = null;
    }
    setUserInput(nextUserInput);
  };

  const sendCurrentUserInput = () => {
    const selectedSuggestion = pendingSuggestionRef.current;
    const matchesSelectedSuggestion =
      selectedSuggestion?.question === (userInput || "").trim();

    void handleSendMessage({
      userInput,
      evidenceRefs: matchesSelectedSuggestion
        ? selectedSuggestion.evidence_refs
        : [],
    });
  };

  // Invalidate the in-flight request: its socket, reconnect timers and
  // buffered frames all become no-ops. Leaves an unfinished answer marked.
  const cancelActiveRequest = () => {
    requestSeqRef.current += 1;
    if (voiceAutoSendTimerRef.current) {
      clearTimeout(voiceAutoSendTimerRef.current);
      voiceAutoSendTimerRef.current = null;
    }
    const socket = wsRef.current;
    wsRef.current = null;
    try {
      socket?.close();
    } catch {}
    setIsLoading(false);
    setIsStreaming(false);
  };

  const closeChat = () => {
    cancelActiveRequest();
    clearSpeechError();
    stopListening();
    stopSpeaking();
    resetVoiceInput();

    setMessages([]);
    setDynamicSuggestions([]);
    setFeedbackTexts({});
    setFeedbackTypes({});
    setSummary("");
    setSummaryVersion(0);
    setIsExpanded(false);
    setIsMinimized(false);
    setHasUnreadAnswer(false);
  };

  const minimizeChat = () => {
    stopListening();
    setIsMinimized(true);
  };

  const restoreChat = () => {
    setIsMinimized(false);
    setHasUnreadAnswer(false);
  };

  const stopStreaming = () => {
    if (!isLoading) return;
    cancelActiveRequest();
    stopSpeaking();
    setMessages((prev) => {
      const lastIndex = prev.length - 1;
      if (prev[lastIndex]?.type !== "answer") return prev;
      const updated = [...prev];
      updated[lastIndex] = { ...updated[lastIndex], stopped: true };
      return updated;
    });
  };

  const handleFeedback = (messageId, feedback, feedbackText) => {
    giveFeedback({
      msgId: messageId,
      feedback,
      feedbackText,
      setFeedbackMessageId,
    });
  };

  const handleFeedbackTextChange = (messageId, text) => {
    setFeedbackTexts((prev) => ({ ...prev, [messageId]: text }));
  };

  const clearPendingFeedback = (messageId) => {
    setFeedbackTexts((prev) => {
      const updated = { ...prev };
      delete updated[messageId];
      return updated;
    });
    setFeedbackTypes((prev) => {
      const updated = { ...prev };
      delete updated[messageId];
      return updated;
    });
  };

  const openFeedbackForm = (messageId, feedbackType) => {
    setFeedbackTypes((prev) => ({ ...prev, [messageId]: feedbackType }));
    setFeedbackTexts((prev) => ({ ...prev, [messageId]: "" }));
  };

  const submitFeedback = (messageId) => {
    const feedback = feedbackTypes[messageId] || "Negative";
    const feedbackText = feedbackTexts[messageId]?.trim() || feedback;
    handleFeedback(messageId, feedback, feedbackText);
    clearPendingFeedback(messageId);
  };

  const getFeedbackPlaceholder = (messageId) =>
    feedbackTypes[messageId] === "Positive"
      ? "Tell us what worked well..."
      : "Tell us what went wrong...";

  const handleProductSuggestionClick = ({ suggestion }) => {
    if (!suggestion) return;

    if (suggestion.isLocked) {
      navigate(`/no-access?product=${encodeURIComponent(suggestion.productName)}`, {
        state: {
          productName: suggestion.productName,
        },
      });
      return;
    }

    navigate(suggestion.route);
  };

  const handleSendMessage = async ({
    userInput: raw,
    evidenceRefs = [],
    silentRetry = false,
    preservedContext = null,
    retryCount = 0,
  }) => {
    let requestIdForCatch = null;
    try {
      clearSpeechError();
      stopListening();
      stopSpeaking();
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
      const trimmed = (raw || "").trim();
      if (!trimmed || (isLoading && !silentRetry)) return;
      const normalizedEvidenceRefs = Array.isArray(evidenceRefs)
        ? evidenceRefs
        : [];
      if (!silentRetry) pendingSuggestionRef.current = null;

      if (onDelegateSend && !silentRetry) {
        const accepted = onDelegateSend({
          userInput: trimmed,
          evidenceRefs: normalizedEvidenceRefs,
        });
        if (accepted) resetVoiceInput();
        return;
      }

      const requestId = requestSeqRef.current + 1;
      requestSeqRef.current = requestId;
      requestIdForCatch = requestId;
      const isStale = () => requestSeqRef.current !== requestId;
      const previousSocket = wsRef.current;
      wsRef.current = null;
      try {
        previousSocket?.close();
      } catch {}

      if (!isExpanded) setIsExpanded(true);
      if (!silentRetry) {
        setIsMinimized(false);
        setHasUnreadAnswer(false);
      }

      setDynamicSuggestions([]);
      setIsLoading(true);
      resetVoiceInput();

      const user_id = getUserId();
      const random_number = Math.floor(100000 + Math.random() * 900000);
      const chat_id = `${origin}-${user_id}-${random_number}`;

      if (!silentRetry) {
        const qMsg = { type: "question", text: trimmed };
        setMessages((prev) => [...prev, qMsg]);
      }

      const lastQAContext =
        typeof preservedContext === "string"
          ? preservedContext
          : messages
              .filter((m) => m.type === "question" || m.type === "answer")
              .slice(-4)
              .map(
                (m) =>
                  `${m.type === "question" ? "Question:" : "Answer:"} ${m.text}`,
              )
              .join(" ") || "";

      if (!silentRetry) {
        setMessages((prev) => [...prev, { type: "answer", text: "" }]);
      }

      const requestBody = {
        query: `question: ${trimmed}`,
        context: lastQAContext,
        document_id: [""],
        dashboard_name: defaultDashboardName,
        countries: defaultCountries,
        company_ids: defaultCompanyIds,
        reference_ids: "",
        evidence_refs: normalizedEvidenceRefs,
        timestamp: new Date().toISOString(),
        chat_id,
        sender: user_id,
        sender_display_name: "USER",
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        search_flag: true,
        show_messages: false,
        origin,
        show_suggestions: showSuggestions,
        summary: summary,
        summary_version: summaryVersion,
        user_language: userLanguage,
        action_mode: true,
      };

      // ----- frame-batched streaming -----
      let rafId = null;
      let streamBuf = "";

      const flushAnswerOncePerFrame = () => {
        if (!streamBuf || isStale()) return;
        const chunk = streamBuf;
        streamBuf = "";

        setMessages((prev) => {
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i]?.type === "answer") {
              updated[i] = {
                ...updated[i],
                text: (updated[i].text || "") + chunk,
              };
              break;
            }
          }
          return updated;
        });
      };

      const scheduleFrameFlush = () => {
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          flushAnswerOncePerFrame();
        });
      };

      const enqueueText = (s) => {
        if (!s) return;
        streamBuf += s;
        scheduleFrameFlush();
      };

      // ----- 25ms burst coalescer -----
      let wsBurstTimer = null;
      let wsBurstBuf = "";

      const addToBurst = (s) => {
        if (!s) return;
        wsBurstBuf += s;
        if (wsBurstTimer) return;
        wsBurstTimer = setTimeout(() => {
          enqueueText(wsBurstBuf);
          wsBurstBuf = "";
          wsBurstTimer = null;
        }, 25);
      };

      let hasFinished = false;
      let roundIsFinalized = false;
      let reconnectEnabled = true;
      let reconnectAttempts = 0;
      const MAX_RECONNECT_ATTEMPTS = 5;
      let lastType = null;
      let activeAssistantMessageId = null;
      let hasRenderableContent = false;
      let autoContinueRounds = 0;

      const finalizeAnswerAndSuggestion = ({ fallbackText = "" } = {}) => {
        if (roundIsFinalized || isStale()) return;
        roundIsFinalized = true;

        if (wsBurstTimer) {
          clearTimeout(wsBurstTimer);
          wsBurstTimer = null;
          enqueueText(wsBurstBuf);
          wsBurstBuf = "";
        }
        flushAnswerOncePerFrame();

        setMessages((prev) => {
          const updated = [...prev];
          let answerIndex = -1;
          for (let i = updated.length - 1; i >= 0; i -= 1) {
            if (updated[i]?.type === "answer") {
              answerIndex = i;
              break;
            }
          }
          if (
            answerIndex === -1 ||
            (!fallbackText &&
              (updated[answerIndex]?.productSuggestion ||
                updated[answerIndex]?.productSuggestions?.length))
          ) {
            return prev;
          }

          if (fallbackText) {
            const currentText = updated[answerIndex]?.text || "";
            if (currentText.trim()) return prev;
            updated[answerIndex] = {
              ...updated[answerIndex],
              text: fallbackText,
            };
            return updated;
          }

          let pairedQuestionText = trimmed;
          for (let i = answerIndex - 1; i >= 0; i -= 1) {
            if (updated[i]?.type === "question") {
              pairedQuestionText = updated[i]?.text || trimmed;
              break;
            }
          }

          const answerText = htmlToSpeechText(updated[answerIndex]?.text || "");
          const productSuggestions = buildProductSuggestionsForPair({
            questionText: pairedQuestionText,
            answerText,
            tabs: availableTabs,
          });

          if (!productSuggestions.length) return prev;
          updated[answerIndex] = {
            ...updated[answerIndex],
            productSuggestions,
          };
          return updated;
        });

        setIsLoading(false);
        setIsStreaming(false);
      };

      const resetReplayState = () => {
        if (isStale()) return;
        if (wsBurstTimer) {
          clearTimeout(wsBurstTimer);
          wsBurstTimer = null;
        }
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        wsBurstBuf = "";
        streamBuf = "";
        lastType = null;
        setIsStreaming(false);
        setMessages((prev) => {
          if (!prev.length) return prev;
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i -= 1) {
            if (updated[i]?.type === "answer") {
              updated[i] = { ...updated[i], text: "" };
              return updated;
            }
          }
          return prev;
        });
      };

      const scheduleReconnect = () => {
        if (
          !reconnectEnabled ||
          hasFinished ||
          roundIsFinalized ||
          isStale()
        ) {
          return;
        }
        if (!activeAssistantMessageId) {
          finalizeAnswerAndSuggestion();
          return;
        }
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          finalizeAnswerAndSuggestion();
          return;
        }
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
        reconnectAttempts += 1;
        window.setTimeout(() => {
          void connectWS(true).catch((error) => {
            console.error("WebSocket reconnect auth/setup error:", error);
            if (!isStale()) {
              scheduleReconnect();
            }
          });
        }, delay);
      };

      // Thin-evidence checkpoint: resolve it without asking the user. Search
      // deeper a bounded number of times, then answer with what we have. The
      // decision is surfaced as a step in the analysis timeline.
      const respondToAction = (action) => {
        const socket = wsRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          finalizeAnswerAndSuggestion();
          return;
        }

        const canAnswerNow = action.actions.includes("answer_now");
        const shouldContinue =
          !canAnswerNow || autoContinueRounds < MAX_AUTO_CONTINUE_ROUNDS;
        const sourceNote =
          action.currentSourceCount != null
            ? ` (${action.currentSourceCount} so far)`
            : "";
        const stepText = shouldContinue
          ? `Evidence is thin${sourceNote}, searching more sources...`
          : `Composing answer from the sources found${sourceNote}...`;

        if (lastType !== "INFO") {
          addToBurst("<CollapsibleDiv>");
          lastType = "INFO";
        }
        addToBurst(`\n${stepText}\n`);
        setIsStreaming(true);

        const payload = {
          type: "action",
          action: shouldContinue ? "continue" : "answer_now",
          resume_id: action.resumeId,
        };
        if (shouldContinue) {
          autoContinueRounds += 1;
          payload.extra_rounds = 1;
        }

        reconnectEnabled = true;
        reconnectAttempts = 0;
        socket.send(JSON.stringify(payload));
      };

      const connectWS = async (isReconnect = false) => {
        if (
          !reconnectEnabled ||
          hasFinished ||
          roundIsFinalized ||
          isStale()
        ) {
          return;
        }

        const wsUrl = await getAuthenticatedWebSocketUrl(
          chat_id,
          user_id,
          isReconnect ? activeAssistantMessageId : undefined,
        );
        if (isStale()) return;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (wsRef.current !== ws) {
            try {
              ws.close();
            } catch {}
            return;
          }
          if (isReconnect) {
            resetReplayState();
            return;
          }
          ws.send(JSON.stringify(requestBody));
        };

        ws.onmessage = (event) => {
          if (wsRef.current !== ws || isStale()) return;

          try {
            const data = JSON.parse(event.data);
            const actionPayload = parseActionPayload(data);

            if (actionPayload) {
              if (wsBurstTimer) {
                clearTimeout(wsBurstTimer);
                wsBurstTimer = null;
                enqueueText(wsBurstBuf);
                wsBurstBuf = "";
              }
              flushAnswerOncePerFrame();
              respondToAction(actionPayload);
              return;
            }

            activeAssistantMessageId =
              data?.message_id ||
              (data.type === "MESSAGE_ID" && data.message != null
                ? String(data.message)
                : activeAssistantMessageId);

            if (data.message && !METADATA_FRAME_TYPES.has(data.type)) {
              if (data.type === "YAHOO_FINANCE_TICKERS") return;
              if (data.type === "SUMMARY_UPDATE") {
                const summaryPayload =
                  typeof data.message === "string"
                    ? JSON.parse(data.message)
                    : data.message;
                setSummary(summaryPayload?.summary || "");
                setSummaryVersion(summaryPayload?.version || 0);
                return;
              }
              if (data.type === "CHART" && lastType !== "CHART")
                addToBurst("<chartrender>");
              else if (lastType === "CHART" && data.type !== "CHART")
                addToBurst("</chartrender>");

              if (data.type === "INFO" && lastType !== "INFO") {
                addToBurst("<CollapsibleDiv>");
                setIsStreaming(true);
              } else if (data.type !== "INFO" && lastType === "INFO") {
                addToBurst("</CollapsibleDiv>");
                setIsStreaming(false);
              }

              if (data.type === "urls") {
                hasRenderableContent = true;
                const urls = String(data.message).split(/\s+/).filter(Boolean);
                addToBurst(
                  `<sourcelist urls="${escapeAttr(urls.join(" "))}"></sourcelist>`,
                );
              } else {
                if (
                  typeof data.message === "string" &&
                  data.message.trim().length > 0
                ) {
                  hasRenderableContent = true;
                }
                addToBurst(data.message);
              }

              lastType = data.type || null;
            }

            if (data.type === "SUGGESTIONS" && data.message) {
              setDynamicSuggestions(getChatSuggestionsFromPayload(data.message));
            }

            if (data.type === "MESSAGE_ID") {
              setMessages((prev) => {
                const updated = [...prev];
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i]?.type === "answer") {
                    updated[i] = { ...updated[i], message_id: data.message };
                    break;
                  }
                }
                return updated;
              });
            }

            if (data.type === "FINISH") {
              hasFinished = true;
              reconnectEnabled = false;
              if (!hasRenderableContent) {
                if (retryCount < EMPTY_RESPONSE_RETRY_LIMIT) {
                  console.warn(
                    "Home chat received an empty assistant response; retrying once.",
                  );
                  try {
                    ws.close();
                  } catch {}
                  window.setTimeout(() => {
                    void handleSendMessageRef.current?.({
                      userInput: trimmed,
                      evidenceRefs: normalizedEvidenceRefs,
                      silentRetry: true,
                      preservedContext: lastQAContext,
                      retryCount: retryCount + 1,
                    });
                  }, 0);
                  return;
                }

                finalizeAnswerAndSuggestion({
                  fallbackText: EMPTY_RESPONSE_FALLBACK_TEXT,
                });
                return;
              }
              finalizeAnswerAndSuggestion();
            }
          } catch (e) {
            console.error("WS parse error:", e);
          }
        };

        ws.onerror = (err) => {
          if (wsRef.current !== ws) return;
          console.error("WebSocket error:", err);
        };

        ws.onclose = () => {
          if (wsRef.current !== ws) return;
          wsRef.current = null;
          if (!hasFinished && !roundIsFinalized && !isStale()) {
            if (reconnectEnabled && activeAssistantMessageId) {
              scheduleReconnect();
              return;
            }
            finalizeAnswerAndSuggestion();
          }
        };
      };
      await connectWS(false);
    } catch (e) {
      console.error("handleSendMessage error:", e);
      if (requestSeqRef.current === requestIdForCatch) {
        setIsLoading(false);
        setIsStreaming(false);
      }
    }
  };

  const askQuestion = (questionText, evidenceRefs = []) => {
    const normalizedQuestion =
      typeof questionText === "string" ? questionText.trim() : "";

    if (!normalizedQuestion || isLoading) return false;

    setUserInput(normalizedQuestion);
    void handleSendMessage({ userInput: normalizedQuestion, evidenceRefs });
    return true;
  };

  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  useEffect(() => {
    if (voiceAutoSendTimerRef.current) {
      clearTimeout(voiceAutoSendTimerRef.current);
      voiceAutoSendTimerRef.current = null;
    }

    if (!isListening || isLoading) return;

    const trimmedVoiceInput = (userInput || "").trim();
    if (!trimmedVoiceInput) return;

    voiceAutoSendTimerRef.current = setTimeout(() => {
      stopListening();
      handleSendMessageRef.current?.({ userInput: trimmedVoiceInput });
      voiceAutoSendTimerRef.current = null;
    }, VOICE_IDLE_AUTO_SEND_MS);

    return () => {
      if (voiceAutoSendTimerRef.current) {
        clearTimeout(voiceAutoSendTimerRef.current);
        voiceAutoSendTimerRef.current = null;
      }
    };
  }, [isListening, isLoading, stopListening, userInput]);

  useEffect(() => {
    sendMessageRef.current = askQuestion;
  }, [askQuestion]);

  useImperativeHandle(
    ref,
    () => ({
      askQuestion: (questionText, evidenceRefs) =>
        sendMessageRef.current?.(questionText, evidenceRefs) ?? false,
      minimize: () => {
        if (isExpanded) setIsMinimized(true);
      },
    }),
    [isExpanded],
  );

  // Copies only the answer body: analysis steps and source chips are excluded.
  const copyToClipboard = async (htmlString, index) => {
    const answerHtml = (htmlString || "")
      .replace(ANALYSIS_BLOCK_RE, "")
      .replace(SOURCE_LIST_RE, "");
    const doc = new DOMParser().parseFromString(answerHtml, "text/html");
    const plainText = (doc.body.innerText || doc.body.textContent || "").trim();

    try {
      await navigator.clipboard.writeText(plainText);
    } catch (error) {
      console.error("Copy failed:", error);
      return;
    }

    setCopiedIndex(index);
    window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => setCopiedIndex(null), 1800);
  };
  const showLauncher =
    !hideLauncher &&
    (!isExpanded || isMinimized || keepLauncherVisibleWhenExpanded);
  const isOverlayExpanded = isExpanded && expandedPresentation === "overlay";
  const showDock = isExpanded && isMinimized;
  const expandedWrapperStyle = isOverlayExpanded
    ? {
        position: "fixed",
        top: `${expandedOverlayTop}px`,
        left: "50%",
        transform: "translateX(-50%)",
        width: expandedOverlayWidth,
        maxWidth: "calc(100vw - 32px)",
        zIndex: 1300,
      }
    : {
        marginTop: "8px",
      };

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="w-100">
      {/* COLLAPSED */}
      {showLauncher && (
        <>
          <HomeChatInputBar
            docked={false}
            placeholder={placeholder}
            placeholderQuestions={mergedPlaceholderSuggestions}
            animatePlaceholder={animatePlaceholder && !isExpanded}
            userInput={userInput}
            setUserInput={handleUserInputChange}
            isLoading={isLoading}
            isListening={isListening}
            isSpeechRecognitionSupported={isSpeechRecognitionSupported}
            onToggleListening={toggleListening}
            onSend={sendCurrentUserInput}
            onStop={stopStreaming}
            onPlaceholderClick={handlePlaceholderClick}
            ui={ui}
          />
          {speechError && (
            <div
              className="px-2 mt-1"
              style={{ fontSize: "12px", color: ui.isDark ? "#F6B0B0" : "#B42318" }}
            >
              {speechError}
            </div>
          )}
          {isListening && !isLoading && (
            <div
              className="px-2 mt-1"
              style={{ fontSize: "12px", color: ui.muted }}
            >
              Listening... auto-send after {VOICE_IDLE_AUTO_SEND_MS / 1000}s of silence.
            </div>
          )}
        </>
      )}

      {/* EXPANDED */}
      {isExpanded && (
        <>
          {isOverlayExpanded && !isMinimized ? (
            <div
              aria-hidden="true"
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(11, 18, 32, 0.18)",
                backdropFilter: "blur(4px)",
                zIndex: 1290,
              }}
            />
          ) : null}
          <div
            style={expandedWrapperStyle}
            className={`ea-chat-panel ${
              isOverlayExpanded ? "ea-chat-panel--overlay" : ""
            }`}
            hidden={isMinimized}
          >
            <div
              className="ea-chat-surface ea-chat-shell"
              style={{
                ...chatSurfaceVars,
                height:
                  typeof expandedHeight === "number"
                    ? `${expandedHeight}px`
                    : expandedHeight,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transition: "height 0.25s ease",
              }}
            >
              <div className="ea-chat-header d-flex align-items-center justify-content-between gap-3">
                <div className="ea-chat-brand">
                  <span className="ea-chat-brand__mark" aria-hidden="true">
                    <img src={emalphaMark} alt="" />
                  </span>
                  <div className="ea-chat-title-group">
                    <p className="ea-chat-title">EMAlpha Assistant</p>
                    <p className="ea-chat-subtitle">
                      Market context, product guidance, and quick follow-ups in one place.
                    </p>
                  </div>
                </div>
                <div className="ea-chat-window-controls">
                  <button
                    type="button"
                    className="ea-chat-close"
                    onClick={minimizeChat}
                    title="Minimize"
                    aria-label="Minimize assistant"
                  >
                    <i className="fa-solid fa-minus" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="ea-chat-close"
                    onClick={closeChat}
                    title="Close (Esc)"
                    aria-label="Close chat"
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div
                className="ea-chat-scroll"
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: "12px",
                }}
              >
            {messages.map((msg, index) => (
              (() => {
                const productSuggestions = Array.isArray(msg.productSuggestions)
                  ? msg.productSuggestions
                  : msg.productSuggestion
                    ? [msg.productSuggestion]
                    : [];

                return (
              <Alert
                key={index}
                variant={
                  ui.isDark
                    ? "dark"
                    : msg.type === "question"
                      ? "primary"
                      : "secondary"
                }
                className={`my-2 border-0 ea-chat-bubble ${
                  msg.type === "question"
                    ? "ea-chat-bubble--question"
                    : "ea-chat-bubble--answer"
                }`}
                style={{
                  maxWidth: msg.type === "question" ? "72%" : "100%",
                  marginLeft: msg.type === "question" ? "auto" : "0",
                  marginRight: msg.type === "question" ? "0" : "auto",
                }}
              >
                <div className="ea-chat-rich">
                  <RenderFromString
                    htmlString={msg.text}
                    isStreaming={
                      isStreaming &&
                      msg.type === "answer" &&
                      index === messages.length - 1
                    }
                    isComplete={
                      !(isLoading && index === messages.length - 1)
                    }
                  />
                  {msg.type === "answer" &&
                    isLoading &&
                    !isStreaming &&
                    index === messages.length - 1 &&
                    (
                      <div
                        className={
                          hasRenderedAnswer(msg.text) ? "ea-chat-generating" : undefined
                        }
                      >
                        <ThinkingIndicator
                          label={getPendingAnswerLabel(msg.text)}
                        />
                      </div>
                    )}
                  {msg.stopped && (
                    <div className="ea-chat-stopped">
                      <i className="fa-solid fa-circle-stop" aria-hidden="true" />
                      Response stopped
                    </div>
                  )}
                </div>

                {msg.type === "answer" &&
                  productSuggestions.length > 0 &&
                  !isLoading && (
                  <div className="mt-3 mb-1">
                    <div className="d-flex flex-wrap gap-2">
                      {productSuggestions.map((suggestion) => (
                        <Button
                          key={suggestion.productKey}
                          className="ea-chat-inline-chip"
                          size="sm"
                          variant={ui.isDark ? "outline-light" : "outline-primary"}
                          onClick={() =>
                            handleProductSuggestionClick({
                              suggestion,
                            })
                          }
                          style={{
                            borderRadius: "999px",
                            fontWeight: 600,
                            borderColor: ui.isDark
                              ? "rgba(76,125,255,0.65)"
                              : undefined,
                            color: ui.isDark ? "#BFD0FF" : undefined,
                          }}
                        >
                          {suggestion.ctaText}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {msg.type === "answer" &&
                  !(isLoading && index === messages.length - 1) &&
                  hasRenderedAnswer(msg.text) && (
                  <>
                    <div className="ea-chat-actions-bar">
                      {msg.message_id &&
                        feedbackMessageId.includes(msg.message_id) && (
                          <span className="ea-chat-feedback-thanks" role="status">
                            <i className="fa-solid fa-check" aria-hidden="true" />
                            Thanks for the feedback
                          </span>
                        )}
                      {msg.message_id &&
                        !feedbackMessageId.includes(msg.message_id) &&
                        [
                          ["Positive", "fa-thumbs-up", "Helpful"],
                          ["Negative", "fa-thumbs-down", "Not helpful"],
                        ].map(([type, icon, label]) => {
                          const isSelected =
                            feedbackTexts[msg.message_id] !== undefined &&
                            feedbackTypes[msg.message_id] === type;
                          return (
                            <button
                              key={type}
                              type="button"
                              className={`ea-chat-copy ${
                                isSelected ? "is-selected" : ""
                              }`}
                              onClick={() =>
                                isSelected
                                  ? clearPendingFeedback(msg.message_id)
                                  : openFeedbackForm(msg.message_id, type)
                              }
                              title={label}
                              aria-label={label}
                              aria-pressed={isSelected}
                            >
                              <i
                                className={`${
                                  isSelected ? "fa-solid" : "fa-regular"
                                } ${icon}`}
                                aria-hidden="true"
                              />
                            </button>
                          );
                        })}
                      <button
                        type="button"
                        className={`ea-chat-copy ${
                          copiedIndex === index ? "is-copied" : ""
                        }`}
                        onClick={() => copyToClipboard(msg.text, index)}
                        title={copiedIndex === index ? "Copied" : "Copy"}
                        aria-label={
                          copiedIndex === index ? "Copied" : "Copy answer"
                        }
                      >
                        <i
                          className={`fa-regular ${
                            copiedIndex === index ? "fa-circle-check" : "fa-copy"
                          }`}
                          aria-hidden="true"
                        />
                        {copiedIndex === index && (
                          <span className="ea-chat-copy__label" role="status">
                            Copied
                          </span>
                        )}
                      </button>
                    </div>

                    {feedbackTexts[msg.message_id] !== undefined && (
                      <form
                        className="ea-chat-feedback"
                        onSubmit={(event) => {
                          event.preventDefault();
                          submitFeedback(msg.message_id);
                        }}
                      >
                        <label
                          className="ea-chat-feedback__title"
                          htmlFor={`feedback-${msg.message_id}`}
                        >
                          {feedbackTypes[msg.message_id] === "Positive"
                            ? "What worked well?"
                            : "What could be better?"}
                          <span className="ea-chat-feedback__optional">
                            Optional
                          </span>
                        </label>
                        <textarea
                          id={`feedback-${msg.message_id}`}
                          className="ea-chat-feedback__input"
                          rows={2}
                          autoFocus
                          placeholder={getFeedbackPlaceholder(msg.message_id)}
                          value={feedbackTexts[msg.message_id]}
                          onChange={(e) =>
                            handleFeedbackTextChange(
                              msg.message_id,
                              e.target.value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              submitFeedback(msg.message_id);
                            }
                          }}
                        />
                        <div className="ea-chat-feedback__actions">
                          <button
                            type="button"
                            className="ea-chat-feedback__btn"
                            onClick={() => clearPendingFeedback(msg.message_id)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="ea-chat-feedback__btn is-primary"
                          >
                            Submit
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                )}
              </Alert>
                );
              })()
            ))}

            {dynamicSuggestions.length > 0 && !isLoading && (
              <div className="mb-2 mt-1">
                <div className="row">
                  {dynamicSuggestions.map((suggestion, idx) => (
                    <div
                      className="col-6 mb-1"
                      key={`${suggestion.question}-${idx}`}
                    >
                      <Button
                        className="w-100 ea-chat-suggestion-btn"
                        onClick={() =>
                          handleSendMessage({
                            userInput: suggestion.question,
                            evidenceRefs: suggestion.evidence_refs,
                          })
                        }
                        variant={ui.isDark ? "outline-light" : "light"}
                        style={{
                          fontSize: "12px",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "normal",
                          lineHeight: "1.3em",
                          height: "3.3em",
                        }}
                      >
                        {suggestion.question}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isLoading && messages[messages.length - 1]?.type !== "answer" && (
              <div className="my-2 ea-chat-loading-bubble">
                <ThinkingIndicator />
              </div>
            )}

            <div ref={messagesEndRef} />
              </div>

              {/* Docked input */}
              {speechError && (
                <div
                  className="px-3 pb-1"
                  style={{ fontSize: "12px", color: ui.isDark ? "#F6B0B0" : "#B42318" }}
                >
                  {speechError}
                </div>
              )}
              {isListening && !isLoading && (
                <div
                  className="px-3 pb-1"
                  style={{ fontSize: "12px", color: ui.muted }}
                >
                  Listening... auto-send after {VOICE_IDLE_AUTO_SEND_MS / 1000}s of silence.
                </div>
              )}
              <HomeChatInputBar
                docked
                placeholder={placeholder}
                placeholderQuestions={mergedPlaceholderSuggestions}
                animatePlaceholder={animatePlaceholder && !isExpanded}
                userInput={userInput}
                setUserInput={handleUserInputChange}
                isLoading={isLoading}
                isListening={isListening}
                isSpeechRecognitionSupported={isSpeechRecognitionSupported}
                onToggleListening={toggleListening}
                onSend={sendCurrentUserInput}
                onStop={stopStreaming}
                onPlaceholderClick={handlePlaceholderClick}
                ui={ui}
              />
              <p className="ea-chat-fineprint">
                EMAlpha Assistant can make mistakes. Verify important information.
              </p>
            </div>
          </div>
        </>
      )}

      {/* MINIMIZED DOCK */}
      {showDock &&
        createPortal(
          <div
            className="ea-chat-surface ea-chat-dock"
            style={chatSurfaceVars}
            role="region"
            aria-label="EMAlpha Assistant (minimized)"
          >
            <button
              type="button"
              className="ea-chat-dock__open"
              onClick={restoreChat}
              ref={dockButtonRef}
              title="Open assistant"
              aria-label={`Open EMAlpha Assistant${
                isLoading
                  ? ", responding"
                  : hasUnreadAnswer
                    ? ", new response"
                    : ""
              }`}
            >
              <span className="ea-chat-dock__mark" aria-hidden="true">
                <img src={emalphaMark} alt="" />
                {(isLoading || hasUnreadAnswer) && (
                  <span
                    className={`ea-chat-dock__badge ${
                      isLoading ? "is-busy" : "is-unread"
                    }`}
                  />
                )}
              </span>
              <span className="ea-chat-dock__text">
                <span className="ea-chat-dock__label">Assistant</span>
                <span className="ea-chat-dock__status" aria-live="polite">
                  {isLoading
                    ? "Responding…"
                    : hasUnreadAnswer
                      ? "New response"
                      : "Conversation open"}
                </span>
              </span>
              <i
                className="fa-solid fa-chevron-up ea-chat-dock__chevron"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="ea-chat-dock__close"
              onClick={closeChat}
              title="End conversation"
              aria-label="End conversation"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
});

HomeChat.displayName = "HomeChat";

export default HomeChat;
